import React, { useState, useEffect } from "react";
import "../result/result.css";

const SubjectWiseResult = () => {
    const [academicYear, setAcademicYear] = useState("");
    const [classValue, setClassValue] = useState("");
    const [subject, setSubject] = useState("");
    const [selectedExamName, setSelectedExamName] = useState("");
    const [studentData, setStudentData] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [marksData, setMarksData] = useState({});
    const [classes, setClasses] = useState([]);
    const [division, setDivision] = useState("");
    const [divisions, setDivisions] = useState([]);
    const [subjectSequence, setSubjectSequence] = useState([]);
    const [serialNoData, setSerialNoData] = useState({});
    const [schoolData, setSchoolData] = useState(null);

    // IndexedDB constants
    const DB_NAME = 'SchoolManagementDB';
    const STUDENT_STORE = 'studentData';
    const SCHOOL_STORE = 'schoolData';
    const DB_VERSION = 1;
    
    const examNameTranslations = {
      "Unit Test I" : "घटक चाचणी-1",
      "Unit Test II" : "घटक चाचणी-2",
      "Semester First ": "प्रथम सत्र",
      "Unit Test III" : "घटक चाचणी-3",
      "Unit Test IV" : "घटक चाचणी-4",
      "Semester Second ": "द्वितीय सत्र",
    };
    
    const udiseNumber = localStorage.getItem("udiseNumber");
    const [language, setLanguage] = useState(localStorage.getItem('language') || 'English');

    // Open IndexedDB connection and load data
    useEffect(() => {
      const openDB = () => {
        return new Promise((resolve, reject) => {
          const request = indexedDB.open(DB_NAME, DB_VERSION);
          
          request.onerror = (event) => {
            console.error("IndexedDB error:", event.target.error);
            reject(event.target.error);
          };
          
          request.onsuccess = (event) => {
            const db = event.target.result;
            resolve(db);
          };
          
          request.onupgradeneeded = (event) => {
            const db = event.target.result;
            
            // Create object stores if they don't exist
            if (!db.objectStoreNames.contains(STUDENT_STORE)) {
              db.createObjectStore(STUDENT_STORE, { keyPath: "id" });
            }
            
            if (!db.objectStoreNames.contains(SCHOOL_STORE)) {
              db.createObjectStore(SCHOOL_STORE, { keyPath: "udiseNumber" });
            }
          };
        });
      };

      const loadData = async () => {
        try {
          const db = await openDB();
          await fetchStudentData(db);
          await fetchSchoolData(db);
        } catch (error) {
          console.error("Error loading data from IndexedDB:", error);
        }
      };

      loadData();
    }, []);

    // Update divisions when class changes
    useEffect(() => {
      if (classValue && serialNoData[classValue]) {
        const divisionsForClass = Object.keys(serialNoData[classValue]);
        setDivisions(divisionsForClass);
      } else {
        setDivisions([]);
      }
      setDivision(""); // Reset division when class changes
    }, [classValue, serialNoData]);

    // Update selected students when division changes
    useEffect(() => {
      if (classValue && division && serialNoData[classValue] && serialNoData[classValue][division]) {
        const serialNumbers = serialNoData[classValue][division];
        if (serialNumbers && serialNumbers.length > 0) {
          // Fetch student details for the selected serial numbers
          fetchSelectedStudents(serialNumbers);
        }
      } else {
        setSelectedStudents([]);
      }
    }, [classValue, division, serialNoData]);

    // Fetch subject sequence when class, division, and academic year are selected
    useEffect(() => {
      if (academicYear && classValue && division) {
        fetchSubjectSequence();
      }
    }, [academicYear, classValue, division]);

    // Fetch marks data when subject, class value, and academic year change
    useEffect(() => {
      if (subject && classValue && division && academicYear && selectedStudents.length > 0) {
        fetchMarksForSelectedSubject();
      }
    }, [subject, classValue, division, academicYear, selectedStudents]);

    useEffect(() => {
      const storedLanguage = localStorage.getItem('language') || 'English';
      setLanguage(storedLanguage);
    }, []);

    // Fetch default academic year from localStorage
    useEffect(() => {
       const fetchDefaultSettings = async () => {
         try {
           const response = await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/defaultSettings.json`);
           if (response.ok) {
             const data = await response.json();
             if (data) {
               setAcademicYear(data.defaultYear || "");
             }
           } else {
             console.error("Failed to fetch default settings.");
           }
         } catch (error) {
           console.error("Error fetching default settings:", error);
         }
       };
   
       fetchDefaultSettings();
     }, [udiseNumber]);

    const handleAcademicYearChange = (e) => setAcademicYear(e.target.value);
    const handleClassChange = (e) => {
      setClassValue(e.target.value);
      setSubject(""); // Reset subject when class changes
    };
    const handleSubjectChange = (e) => setSubject(e.target.value);

    // Fetch student data from IndexedDB
    const fetchStudentData = async (db) => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STUDENT_STORE, "readonly");
        const store = transaction.objectStore(STUDENT_STORE);
        const request = store.getAll();
        
        request.onsuccess = (event) => {
          const students = event.target.result;
          setStudentData(students);
          
          // Organize students by class and division for serialNoData
          const classesAndDivisions = {};
          students.forEach((student) => {
            if (student && student.currentClass) {
              if (!classesAndDivisions[student.currentClass]) {
                classesAndDivisions[student.currentClass] = {};
              }
              
              const division = student.division || "";
              if (!classesAndDivisions[student.currentClass][division]) {
                classesAndDivisions[student.currentClass][division] = [];
              }
              
              // Use the ID as the serial number equivalent
              classesAndDivisions[student.currentClass][division].push(student.id);
            }
          });

          setSerialNoData(classesAndDivisions);
          setClasses(Object.keys(classesAndDivisions));
          resolve(students);
        };
        
        request.onerror = (event) => {
          console.error("Error fetching student data from IndexedDB:", event.target.error);
          reject(event.target.error);
        };
      });
    };

    // Fetch school data from IndexedDB
    const fetchSchoolData = async (db) => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(SCHOOL_STORE, "readonly");
        const store = transaction.objectStore(SCHOOL_STORE);
        const request = store.get(udiseNumber);
        
        request.onsuccess = (event) => {
          const data = event.target.result;
          setSchoolData(data);
          resolve(data);
        };
        
        request.onerror = (event) => {
          console.error("Error fetching school data from IndexedDB:", event.target.error);
          reject(event.target.error);
        };
      });
    };

    // Fetch specific students by their IDs
   // Update the fetchSelectedStudents function to ensure we have all the data
const fetchSelectedStudents = async (studentIds) => {
  try {
    // Filter from already loaded student data
    const filteredStudents = studentData.filter(student => 
      studentIds.includes(student.id)
    );
    
    // Sort students by roll number for better display
    const sortedStudents = filteredStudents.sort((a, b) => {
      const rollNoA = parseInt(a.rollNo) || 0;
      const rollNoB = parseInt(b.rollNo) || 0;
      return rollNoA - rollNoB;
    });
    
    setSelectedStudents(sortedStudents);
    
    // If we have selected students, subject and academic year, fetch marks
    if (sortedStudents.length > 0 && subject && academicYear) {
      await fetchMarksForSelectedSubject(sortedStudents);
    }
  } catch (error) {
    console.error("Error fetching selected students:", error);
    setSelectedStudents([]);
  }
};

useEffect(() => {
  console.log("Current marks data:", marksData);
  console.log("Selected students:", selectedStudents.map(s => `${s.id}: ${s.stdName}`));
}, [marksData, selectedStudents]);

// Helper function to check if any marks exist for display
const hasMarksData = () => {
  return Object.keys(marksData).length > 0 && 
         Object.values(marksData).some(studentMarks => 
           Object.keys(studentMarks).length > 0
         );
};

    // Fetch subject sequence
    const fetchSubjectSequence = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/subjectSequence/icse/${academicYear}/${classValue}/${division}.json`
        );
  
        if (!response.ok) {
          throw new Error('Failed to fetch subject sequence');
        }
        const data = await response.json();
  
        // Check if data is valid before proceeding
        if (data && typeof data === 'object') {
          const orderedSubjects = Object.keys(data)
            .filter(key => data[key]) // Filter out null or empty subjects
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((key) => data[key]);
          setSubjectSequence(orderedSubjects);
        } else {
          console.error('Invalid data format for subject sequence:', data);
          setSubjectSequence([]); // Reset to empty if data is invalid
        }
      } catch (error) {
        console.error('Error fetching subject sequence:', error);
        setSubjectSequence([]); // Reset to empty on error
      }
    };
  

    // Fetch marks for the selected subject
    const fetchMarksForSelectedSubject = async () => {
      try {
        const marksByStudent = {};
        
        // Loop through selected students
        for (const student of selectedStudents) {
          const studentMarks = {};
          
          // Check if the student has result data for the selected academic year
          if (student.icseResult && student.icseResult[academicYear]) {
            // Loop through each exam (Unit Test I, etc.)
            Object.keys(student.icseResult[academicYear]).forEach(examName => {
              // Check if the exam contains the selected subject
              if (student.icseResult[academicYear][examName] && 
                  student.icseResult[academicYear][examName][subject]) {
                
                // Get subject marks
                const subjectMarks = student.icseResult[academicYear][examName][subject];
                
                // Add to the student's marks
                studentMarks[examName] = {
                  outOf: subjectMarks.outOf,
                  obtainMarks: subjectMarks.obtainMarks,
                };
              }
            });
          }
          
          // Add the student's marks to the marks data
          marksByStudent[student.id] = studentMarks;
        }
        
        // Update the state once after processing all students
        setMarksData(marksByStudent);
      } catch (error) {
        console.error('Error fetching marks data:', error);
      }
    };


    const handlePrint = () => {
      const tableElement = document.getElementById("printableTable");
      if (tableElement) {
        const tableContent = tableElement.outerHTML;
    
        const schoolName = schoolData?.schoolName || "";
        const schoolLogo = schoolData?.schoolLogo || ""; // Placeholder if no logo is available
    
        const printWindow = window.open("", "", "height=600,width=800");
        printWindow.document.write("<html><head><title>Print</title>");
    
        // Include your styles here
        printWindow.document.write(`
          <style>
          @page {
              size: A4 Landscape; 
            margin: 3mm;
          }
              table {
                  width: 100%;
                  border-collapse: collapse;
              }
              th, td {
                  border: 1px solid #ccc;
                  padding: 5px;
                  text-align: left;
                  
              }
              thead th {
                  background-color: #f4f4f4;
                  font-weight: normal; /* Removes bold styling */
              }
              tbody td {
                  text-align: center;
                  font-weight: normal; /* Removes bold styling */
              }
              .grade-table table {
                  width: 100%;
                  border-collapse: collapse;
              }
              .grade-table th, .grade-table td {
                  border: 1px solid #ccc;
                  padding: 5px;
                  text-align: center;

              }
                  th{
                    text-align: center;
                    align-items: center;
                  }
              .grade-table thead th {
                  background-color: #f4f4f4;
                  text-align: center;
              }
              .grade-table tbody td {
                  text-align: center;
              }
                .school-header {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                margin-bottom: 20px;
                padding: 10px;
                border: 1px solid #ccc; /* Add border to the header */
              }
              .school-header img {
                max-height: 70px; /* Adjust as needed */
                margin-right: 15px; /* Spacing between logo and school name */
              }
              .school-header h1 {
                font-size: 24px; /* Increased font size for school name */
                margin: 0;
                text-align: left; /* Align text to the left */
                flex: 1; /* Take up remaining space */
                text-overflow: ellipsis; /* Add ellipsis if text overflows */
                white-space: nowrap; /* Prevent text from wrapping to next line */
              }
              .school-header h1.multi-line {
                text-align: center; /* Center text if it wraps to next line */
                white-space: normal; /* Allow text to wrap to next line */
              }
              .school-header-container {
                margin-bottom: 20px;
              }
          </style>
      `);
        printWindow.document.write("</head><body>");
          
        // Add school header with increased text size and logo to the left
        const img = printWindow.document.createElement("img");
        img.src = schoolLogo;
        img.alt = "School Logo";
    
        img.onload = function() {
          printWindow.document.write(`
            <div class="school-header-container">
              <div class="school-header">
                <img src="${schoolLogo}" alt="School Logo">
                <h1 class="${schoolName.length > 30 ? 'multi-line' : ''}">${schoolName}</h1>
                <p>Class: ${classValue} ${division}</p>
                <p>Subject: ${subject}</p>
              </div>
            </div>
          `);
    
          // Add the table content
          printWindow.document.write(tableContent);
    
          printWindow.document.write("</body></html>");
          printWindow.document.close();
          printWindow.print();
        };
    
        img.onerror = function() {
          // If the logo fails to load, proceed without the logo
          printWindow.document.write(`
            <div class="school-header-container">
              <div class="school-header">
                <h1 class="${schoolName.length > 30 ? 'multi-line' : ''}">${schoolName}</h1>
                <p>Class: ${classValue} ${division}</p>
                <p>Subject: ${subject}</p>
              </div>
            </div>
          `);
    
          // Add the table content
          printWindow.document.write(tableContent);
    
          printWindow.document.write("</body></html>");
          printWindow.document.close();
          printWindow.print();
        };
      } else {
        console.error("Table element with ID 'printableTable' not found.");
      }
    };
 
    
    return (
        <div>
   
            <div className="p-3 main-content-of-page">
              <h3 style={{color:'rgb(3, 54, 94)'}} className="title"> {language === "English" ? "Subject Wise" : "विषय निहाय निकाल"}</h3>

              <table className="table table-bordered" style={{maxWidth:'700px'}}>
                <tbody>
                  <tr>
                    <th style={{width:'200px'}}>{language === "English" ? "Academic Year " : "शैक्षणिक वर्ष"}</th>
                    <td>
                      <select
                        id="academicYear"
                        value={academicYear}
                        onChange={handleAcademicYearChange}
                        className="form-control custom-select"
                      >
                        <option >{language === "English" ? "Select Year " : "वर्ष निवडा"}</option>
                        <option value="2023-2024">2023-2024</option>
                        <option value="2024-2025">2024-2025</option>
                        <option value="2025-2026">2025-2026</option>
                        <option value="2026-2027">2026-2027</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th>{language === "English" ? "Class" : "वर्ग"}</th>
                    <td>
                      <select
                        id="class"
                        value={classValue}
                        onChange={handleClassChange}
                        className="form-control custom-select"
                      >
                        <option value="">{language === "English" ? "Select Class" : "वर्ग निवडा"}</option>
                        {classes.map((cls, index) => (
                          <option key={index} value={cls}>
                            {cls}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th>{language === "English" ? "Division " : "तुकडी"}</th>
                    <td>
                      <select
                        id="division"
                        value={division}
                        onChange={(e) => setDivision(e.target.value)}
                        className="form-control custom-select"
                      >
                        <option value="">{language === "English" ? "Select Division " : "तुकडी निवडा"}</option>
                        {divisions.map((div, index) => (
                          <option key={index} value={div}>
                            {div}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th>{language === "English" ? "Subject" : "विषय"}</th>
                    <td>
                      <select
                        id="subject"
                        value={subject}
                        onChange={handleSubjectChange}
                        className="form-control custom-select"
                      >
                        <option value="">{language === "English" ? "Select Subject" : "विषय निवडा"}</option>
                        {(subjectSequence).map((sub, index) => (
                          <option key={index} value={sub}>
                            {sub}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                  <td colSpan="2">
                      <button onClick={handlePrint} className="btn btn-primary">
                        {language === "English" ? "Print" : "Print करा"}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
               
              <br/>

              {/* Add this condition before rendering the table */}
{selectedStudents.length > 0 && subject ? (
  <div className="table-responsive">
    <table className="table table-striped table-bordered" id="printableTable">
      <thead>
        <tr>
          <th rowSpan={2} className="text-center">
            {language === "English" ? "Roll No" : "हजेरी नं."}
          </th>
          <th rowSpan={2} className="text-center">
            {language === "English" ? "Name" : "नाव"}
          </th>

          {Object.keys(examNameTranslations).filter((examName) => {

            return Object.values(marksData).some((marks) => 
              marks && marks[examName] && 
              (marks[examName].outOf !== undefined || marks[examName].obtainMarks !== undefined)
            );
          }).map((examName, index) => (
            <React.Fragment key={index}>
              <th colSpan="2" className="text-center">
                {language === "English" ? examName : examNameTranslations[examName]}
              </th>
            </React.Fragment>
          ))}
          {hasMarksData() && (
            <th colSpan="2" className="text-center">
              {language === "English" ? "Total Marks" : "एकूण गुण"}
            </th>
          )}
        </tr>
        <tr>
          {Object.keys(examNameTranslations).filter((examName) => {
            return Object.values(marksData).some((marks) => 
              marks && marks[examName] && 
              (marks[examName].outOf !== undefined || marks[examName].obtainMarks !== undefined)
            );
          }).map((examName, index) => (
            <React.Fragment key={index}>
              <th style={{fontWeight: 'normal', fontSize:'14px'}}>M.M.</th>
              <th style={{fontWeight: 'normal', fontSize:'14px'}}>Obt. M.</th>
            </React.Fragment>
          ))}
          {hasMarksData() && (
            <>
              <th style={{fontWeight: 'normal', fontSize:'14px'}}>M.M.</th>
              <th style={{fontWeight: 'normal', fontSize:'14px'}}>Obt. M.</th>
            </>
          )}
        </tr>
      </thead>

   <tbody>
  {selectedStudents
    .filter(student => student.isActive !== false)  // Filter out inactive students
    .map((student) => {
      const availableExams = Object.keys(examNameTranslations).filter((examName) => {
        return Object.values(marksData).some((marks) => 
          marks && marks[examName] && 
          (marks[examName].outOf !== undefined || marks[examName].obtainMarks !== undefined)
        );
      });
      
      // Calculate totals
      const totalOutOf = availableExams.reduce((acc, examName) => {
        return acc + (marksData[student.id]?.[examName]?.outOf || 0);
      }, 0);
      
      const totalObtained = availableExams.reduce((acc, examName) => {
        return acc + (marksData[student.id]?.[examName]?.obtainMarks || 0);
      }, 0);
      
      return (
        <tr key={student.id}>
          <td>{student.rollNo}</td>
          <td>{student.stdName} {student.stdFather} {student.stdSurname}</td>
          {availableExams.map((examName, index) => (
            <React.Fragment key={index}>
              <td>{marksData[student.id]?.[examName]?.outOf || "AB"}</td>
              <td>{marksData[student.id]?.[examName]?.obtainMarks || "AB"}</td>
            </React.Fragment>
          ))}
          {hasMarksData() && (
            <>
              <td>{totalOutOf}</td>
              <td>{totalObtained}</td>
            </>
          )}
        </tr>
      );
    })}
</tbody>
    </table>
  </div>
) : (
  subject ? (
    <div className="alert alert-info">
      {language === "English" ? 
        "No marks data available for the selected criteria." : 
        "निवडलेल्या निकषांसाठी कोणतेही गुण डेटा उपलब्ध नाही."}
    </div>
  ) : (
    selectedStudents.length > 0 && (
      <div className="alert alert-info">
        {language === "English" ? 
          "Please select a subject to view marks." : 
          "गुण पाहण्यासाठी कृपया एक विषय निवडा."}
      </div>
    )
  )
)}
              <style jsx>{`
                th, td{
                  width:auto;
                }

                table{
                  margin-bottom:20px;
                }
              `}</style>
            </div>
        </div>
    );
};

export default SubjectWiseResult;